// PortfolioBrandMark — draws one brand's mark in a Past collaborations cell
// (EPD-10121). A resolved logo (EPD-12217) is drawn as an image sized to the
// cell; if it fails to load, or there is no logo, the brand name is drawn as a
// wordmark in the heading font (see lib/portfolio/brand-marks).
//
// The wordmark is set at 18px and scaled down (never up, floor 0.5×) to fit
// its cell. `transform: scale` doesn't change the span's layout width, so its
// natural width can be re-read on every resize without a measure → style →
// measure feedback loop. The scale is written straight to the span's style
// from the ResizeObserver callback (after layout, no React re-render); React
// never sets `transform` on it, so a re-render can't clobber it.

import { proxyImageUrl } from "@/lib/image-url";
import {
  fitScale,
  logoBox,
  type BrandMark,
  type LogoMark,
  type Wordmark,
} from "@/lib/portfolio/brand-marks";
import { cn } from "@/lib/utils";
import { useLayoutEffect, useRef, useState, type RefObject } from "react";

/**
 * Keep the mark scaled to fit `cell`'s content box. Observes the cell (the
 * grid reflowing, the breakpoint switching column counts) AND the mark itself,
 * whose natural width changes when a web font finishes loading or the creator
 * picks another heading font in the Design panel.
 *
 * The cell is passed as an element (from a callback ref held in state), not a
 * ref object: a parent's ref isn't attached yet when a child's layout effect
 * first runs.
 */
function useFitToCell(
  cell: HTMLElement | null,
  markRef: RefObject<HTMLElement | null>,
) {
  useLayoutEffect(() => {
    const mark = markRef.current;
    if (!cell || !mark) return;

    const apply = () => {
      // clientWidth includes padding; the mark only gets the content box.
      const style = getComputedStyle(cell);
      const available =
        cell.clientWidth -
        (parseFloat(style.paddingLeft) || 0) -
        (parseFloat(style.paddingRight) || 0);
      const scale = fitScale(available, mark.offsetWidth);
      const transform = scale === 1 ? "" : `scale(${scale})`;
      if (mark.style.transform !== transform) mark.style.transform = transform;
    };
    apply();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(apply);
    observer.observe(cell);
    observer.observe(mark);
    return () => observer.disconnect();
  }, [cell, markRef]);
}

interface PortfolioBrandMarkProps {
  mark: BrandMark;
  /** The grid cell the mark must fit inside (null until it mounts). */
  cell: HTMLElement | null;
  /** Hover/active cells show the mark at full strength. */
  className?: string;
}

export function PortfolioBrandMark({
  mark,
  cell,
  className,
}: PortfolioBrandMarkProps) {
  // Track failed srcs (not a flag or an index) so a new logo URL gets a fresh
  // try, and a failed full logo steps on to the stored icon.
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
  const src =
    mark.kind === "logo"
      ? mark.sources.find((candidate) => !failed.has(candidate))
      : undefined;

  if (mark.kind === "logo" && src) {
    return (
      <BrandLogo
        key={src}
        name={mark.name}
        src={src}
        className={className}
        onError={() => setFailed((prev) => new Set(prev).add(src))}
      />
    );
  }
  return (
    <BrandWordmark
      mark={mark.kind === "logo" ? mark.fallback : mark}
      cell={cell}
      className={className}
    />
  );
}

/**
 * A logo image sized to the same visual area as every other brand's
 * (`logoBox`, EPD-12333), so a long wordmark and a compact mark carry about
 * the same weight; `max-w-full` still keeps it inside a narrow cell. Until it
 * loads (its real proportions unknown) it sits in a 40px-tall band. No
 * `referrerPolicy="no-referrer"`: the logo CDN serves hotlinked images only
 * to requests that carry a Referer.
 */
function BrandLogo({
  name,
  src,
  className,
  onError,
}: {
  name: LogoMark["name"];
  src: string;
  className?: string;
  onError: () => void;
}) {
  const [box, setBox] = useState<{ width: number; height: number } | null>(
    null,
  );
  return (
    <img
      src={proxyImageUrl(src, 200) ?? src}
      alt={`${name} logo`}
      loading="lazy"
      decoding="async"
      onError={onError}
      onLoad={(event) =>
        setBox(
          logoBox(
            event.currentTarget.naturalWidth,
            event.currentTarget.naturalHeight,
          ),
        )
      }
      style={box ? { width: box.width, height: box.height } : undefined}
      className={cn(
        "block max-w-full object-contain opacity-80 transition-opacity duration-150",
        !box && "h-auto max-h-10 w-auto",
        className,
      )}
    />
  );
}

function BrandWordmark({
  mark,
  cell,
  className,
}: {
  mark: Wordmark;
  cell: HTMLElement | null;
  className?: string;
}) {
  const markRef = useRef<HTMLSpanElement>(null);
  useFitToCell(cell, markRef);

  return (
    <>
      <span
        ref={markRef}
        aria-hidden="true"
        className={cn(
          "inline-block whitespace-nowrap leading-none opacity-80 transition-opacity duration-150",
          className,
        )}
        style={{
          fontFamily: mark.fontFamily,
          fontWeight: mark.fontWeight,
          fontStyle: mark.fontStyle,
          textTransform: mark.textTransform,
          letterSpacing: mark.letterSpacing,
          fontSize: "18px",
          color: "var(--pf-heading)",
        }}
      >
        {mark.text}
      </span>
      {/* The drawn text is hidden from screen readers and the full brand name
          is announced instead (a logo's alt text is "{name} logo"). */}
      <span className="sr-only">{mark.name}</span>
    </>
  );
}
